import React, { useState, useCallback } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { makeStyles } from 'tss-react/mui';
import { darkGreyBackground } from 'styles/vars';
import GVTextField from 'components/lib/GVTextField/GVTextField';
import type { ProfileEditorProps, ProfileFields, BarcodeProfileFields } from './types';
import GraphicProfileEditor from './GraphicProfileEditor';
import BarcodeProfileEditor from './BarcodeProfileEditor';
import styles from './ProfileEditorModal.module.css';

const useStyles = makeStyles()((theme) => ({
  dialog: {
    '& .MuiDialog-paper': {
      maxWidth: '900px',
      backgroundColor: darkGreyBackground,
      backgroundImage: 'none',
    },
  },
  dialogTitle: {
    padding: '16px 24px 16px 24px',
  },
  dialogTitleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
  },
  dialogTitleText: {
    flex: '1 1 auto',
    minWidth: 0,
    margin: 0,
    fontSize: theme.typography.h6.fontSize,
    fontWeight: theme.typography.h6.fontWeight,
    lineHeight: theme.typography.h6.lineHeight,
    letterSpacing: theme.typography.h6.letterSpacing,
  },
  closeButton: {
    flexShrink: 0,
    color: theme.palette.primary.main,
  },
  dialogContent: {
    padding: '0 24px 24px 24px',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: darkGreyBackground,
    '& .MuiFormHelperText-root:not(.Mui-error)': {
      color: '#8a8c9f',
    },
    '& .MuiInputBase-root': {
      backgroundColor: `${darkGreyBackground} !important`,
      '&.Mui-focused': {
        backgroundColor: `${darkGreyBackground} !important`,
      },
      '&.Mui-disabled': {
        backgroundColor: `${darkGreyBackground} !important`,
      },
    },
    '& .MuiInputBase-root > input:-webkit-autofill': {
      WebkitBoxShadow: `0 0 0px 1000px ${darkGreyBackground} inset`,
    },
  },
  dialogActions: {
    padding: '16px 24px 24px 24px',
  },
  editorArea: {
    flex: 1,
    overflow: 'hidden',
    minHeight: 0,
    '--gds-color-field-background-default': 'var(--gds-color-cool-gray-600)',
    '--gds-size-field-container-min-height': '64px', // Retain setting of the current size of the field container to avoid layout shift due to message presence
    '--gds-spacing-field-container-margin': '0',
    '--gds-spacing-field-message-margin': 'var(--gds-spacing-xs) var(--gds-spacing-md) 0 var(--gds-spacing-md)',
  },
  editorAreaGraphic: {
    flex: '0 0 auto',
  },
}));

const ProfileEditorModal: React.FC<ProfileEditorProps> = ({
  open,
  onClose,
  onSave,
  profile,
  profileType,
  initialProfileName = '',
}) => {
  const { classes, cx } = useStyles();
  const [profileName, setProfileName] = useState(initialProfileName);
  const [currentFields, setCurrentFields] = useState<ProfileFields | BarcodeProfileFields | null>(null);
  const [hasErrors, setHasErrors] = useState(false);

  const handleFieldsChange = useCallback((fields: ProfileFields | BarcodeProfileFields) => {
    setCurrentFields(fields);
  }, []);

  const handleValidChange = useCallback((errors: boolean) => {
    setHasErrors(errors);
  }, []);

  // Sync profile name when the prop changes (e.g. switching from one profile to another)
  React.useEffect(() => {
    setProfileName(profile?.customProfileName ?? initialProfileName);
  }, [profile, initialProfileName]);

  const handleSave = () => {
    if (currentFields) {
      onSave(profileName, currentFields);
    }
  };

  const title = profile
    ? `Edit ${profileType === 'graphic' ? 'Graphic' : 'Barcode'} Profile`
    : `Create ${profileType === 'graphic' ? 'Graphic' : 'Barcode'} Profile`;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth={false} className={classes.dialog}>
      <DialogTitle className={classes.dialogTitle}>
        <Box className={classes.dialogTitleRow} component="div">
          <Box className={classes.dialogTitleText} component="span">
            {title}
          </Box>
          <IconButton
            aria-label="Close"
            className={classes.closeButton}
            data-testid="close-profile-editor-modal"
            edge="end"
            onClick={onClose}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <GVTextField
          label="Profile Name"
          value={profileName}
          onValueChange={(value) => setProfileName(value ?? '')}
          required
          className={styles.field}
          data-testid="profile-name"
        />
        <div className={cx(classes.editorArea, profileType === 'graphic' && classes.editorAreaGraphic)}>
          {profileType === 'graphic' ? (
            <GraphicProfileEditor
              profile={profile}
              onFieldsChange={handleFieldsChange}
              onValidChange={handleValidChange}
            />
          ) : (
            <BarcodeProfileEditor
              profile={profile}
              onFieldsChange={handleFieldsChange}
              onValidChange={handleValidChange}
            />
          )}
        </div>
      </DialogContent>
      <DialogActions className={classes.dialogActions}>
        <Button
          onClick={onClose}
          color="primary"
          data-testid={profileType === 'graphic' ? 'cancel-graphic-profile-button' : 'cancel-barcode-profile-button'}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="secondary"
          disabled={!profileName.trim() || hasErrors || !currentFields}
          data-testid={profileType === 'graphic' ? 'save-graphic-profile-button' : 'save-barcode-profile-button'}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProfileEditorModal;
